import { createGlobalStyle } from "styled-components";
import { DefaultTheme } from "styled-components";

export const GlobalStyles = createGlobalStyle<{ theme: DefaultTheme }>`
  :root {
    /* Spacing */
    --spacing-1: 0.25rem;
    --spacing-2: 0.5rem;
    --spacing-3: 1rem;
    --spacing-4: 1.5rem;
    --spacing-5: 2rem;
    --spacing-6: 3rem;
    --spacing-7: 4rem;
    --spacing-8: 6rem;

    /* Breakpoints */
    --mobile: 20rem;    /* 320px */
    --tablet: 48rem;    /* 768px */
    --laptop: 64rem;    /* 1024px */
    --desktop: 90rem;   /* 1440px */
    
    /* Typography */
    --font-size-base: 1rem;
    --font-size-sm: 0.875rem;
    --font-size-lg: 1.125rem;
    --font-size-xl: 1.25rem;
    --font-size-2xl: 1.5rem;
    --font-size-3xl: 2rem;
    
    /* Container */
    --container-padding: clamp(1rem, 5vw, 3rem);
    --container-max-width: 90rem;

    /* Z-index layers */
    --z-modal: 1000;
    --z-overlay: 900;
    --z-dropdown: 800;
    --z-header: 700;
    --z-footer: 600;
  }

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  html {
    font-size: 16px;
    scroll-behavior: smooth;
  }

  body {
    font-family: 'Poppins', 'Inter', sans-serif;
    background-color: ${({ theme }) => theme.colors.background};
    color: ${({ theme }) => theme.colors.textPrimary};
    line-height: 1.5;
    min-height: 100vh;
    overflow-x: hidden;
    transition: background-color 0.3s ease, color 0.3s ease;
  }

  a {
    color: inherit;
    text-decoration: none;
  }

  button {
    background: none;
    border: none;
    cursor: pointer;
    font: inherit;
    color: inherit;
  }

  img {
    max-width: 100%;
    height: auto;
  }

  ul, ol {
    list-style: none;
  }

  h1, h2, h3, h4, h5, h6 {
    font-weight: 600;
    line-height: 1.2;
  }

  /* Variables CSS dynamiques basées sur le thème */
  :root {
    --color-background: ${({ theme }) => theme.colors.background};
    --color-background-secondary: ${({ theme }) => theme.colors.backgroundSecondary};
    --color-background-light: ${({ theme }) => theme.colors.backgroundLight};
    --color-background-hover: ${({ theme }) => theme.colors.backgroundHover};
    
    --color-primary: ${({ theme }) => theme.colors.primary};
    --color-primary-light: ${({ theme }) => theme.colors.primaryLight};
    
    --color-text-primary: ${({ theme }) => theme.colors.textPrimary};
    --color-text-secondary: ${({ theme }) => theme.colors.textSecondary};
    --color-text-inverse: ${({ theme }) => theme.colors.textInverse};
    
    --color-border: ${({ theme }) => theme.colors.border};
    --color-error: ${({ theme }) => theme.colors.error};
    --color-success: ${({ theme }) => theme.colors.success};
    --color-warning: ${({ theme }) => theme.colors.warning};
    --color-info: ${({ theme }) => theme.colors.info};

    --border-radius: ${({ theme }) => theme.borderRadius.default};
    --border-radius-sm: ${({ theme }) => theme.borderRadius.sm};
    --border-radius-lg: ${({ theme }) => theme.borderRadius.lg};
    --border-radius-xl: ${({ theme }) => theme.borderRadius.xl};

    --shadow-sm: ${({ theme }) => theme.shadows.sm};
    --shadow: ${({ theme }) => theme.shadows.default};
    --shadow-md: ${({ theme }) => theme.shadows.md};
    --shadow-lg: ${({ theme }) => theme.shadows.lg};
  }

  /* Utility classes */
  .container {
    max-width: var(--container-max-width);
    margin: 0 auto;
    padding: 0 var(--container-padding);
  }

  /* Hide elements on mobile */
  @media (maxWidth: var(--mobile)) {
    .hidden-mobile {
      display: none !important;
    }
  }

  /* Google Translate Styles */
  .goog-te-gadget {
    font-family: var(--font-family-primary) !important;
    color: var(--color-text-primary) !important;
  }

  .goog-te-gadget-simple {
    background-color: transparent !important;
    border: none !important;
    padding: 0 !important;
    font-size: 14px !important;
    line-height: 2em !important;
    display: inline-block;
    cursor: pointer;
    zoom: 1;
  }

  .goog-te-menu-value {
    color: var(--color-text-primary) !important;
    
    &:hover {
      text-decoration: none;
    }
  }

  .goog-te-banner-frame.skiptranslate {
    display: none !important;
  } 

  /* Styles pour Google Translate */
  .goog-te-gadget {
    font-family: var(--font-family-primary) !important;
    color: var(--color-text-primary) !important;
  }

  /* Masquer les éléments inutiles de Google Translate */
  .goog-te-banner-frame,
  .goog-te-balloon-frame,
  #goog-gt-tt,
  .goog-te-balloon-frame,
  .goog-tooltip,
  .goog-tooltip:hover,
  .goog-logo-link,
  .goog-te-gadget > span,
  .goog-te-gadget .goog-te-combo + span,
  .VIpgJd-ZVi9od-l4eHX-hSRGPd,
  .VIpgJd-ZVi9od-ORHb-OEVmcd {
    display: none !important;
  }

  .VIpgJd-ZVi9od-SmfZ-OEVmcd {
    display: none !important;
  }

  /* Styles pour le sélecteur de langue */
  .goog-te-combo {
    background-color: var(--color-background) !important;
    color: var(--color-text-primary) !important;
    border: 1px solid var(--color-border) !important;
    padding: 8px !important;
    border-radius: 4px !important;
    cursor: pointer !important;
    font-size: 14px !important;
    font-family: var(--font-family-primary) !important;
    outline: none !important;
    min-width: 150px !important;
    
    &:hover {
      border-color: var(--color-primary) !important;
    }
    
    &:focus {
      border-color: var(--color-primary) !important;
      box-shadow: 0 0 0 2px var(--color-primary-light) !important;
    }
  }

  /* Masquer la barre supérieure de traduction */
  .skiptranslate,
  .goog-te-banner-frame {
    display: none !important;
  }

  /* Réinitialiser le décalage du body causé par Google Translate */
  body {
    top: 0 !important;
  }
`;
